import { useAutoAnimate } from "@formkit/auto-animate/react";
import {
  leadSourceListAsPublicQueryOptions,
  useMutationLeadCreateAsPublic,
} from "@gymflow/api";
import { cn } from "@gymflow/helpers";
import { LeadSourceDTO } from "@gymflow/types";
import { validatePhoneNumber } from "@gymflow/validation";
import { InfiniteData, infiniteQueryOptions } from "@tanstack/react-query";
import { FormikProvider, useFormik } from "formik";
import QueryString from "qs";
import { useTranslation } from "react-i18next";
import { z } from "zod";
import { toFormikValidationSchema } from "zod-formik-adapter";

import { usePortalRoutes, useRedirectUrl } from "../../hooks";
import { useClubSettings } from "../../providers";
import useGymflowModels from "../../store";
import { Button, FormInput, FormSelectInput, LayeredCircles } from "../atoms";
import { FormNotificationPreferences } from "../atoms/form/FormNotificationPreferences";
import { FormPhoneNumberInput } from "../atoms/form/text/FormPhoneNumberInput";
import CheckmarkIcon from "../atoms/icons/CheckmarkIcon";

export type HostedPagesLeadCaptureProps = {};

const contactUsSchema = z.object({
  email: z.string().trim().toLowerCase().email(),
  firstName: z.string().min(1, "First name is required"),
  lastName: z.string().min(1, "Last name is required"),
  mobileNumber: z
    .string()
    .refine((e) => validatePhoneNumber(e), "Invalid Phone Number"),
  // notes: z.string(),
  leadSource: z.custom<LeadSourceDTO>(
    (e) => !!(e as LeadSourceDTO)?.id,
    "Required",
  ),
  emailCommunication: z.boolean(),
  smsCommunication: z.boolean(),
});
type ContactUsType = z.infer<typeof contactUsSchema>;
const HostedPagesLeadCapture = ({}: HostedPagesLeadCaptureProps) => {
  const urlParams = QueryString.parse(window.location.search, {
    ignoreQueryPrefix: true,
  }) as {
    leadSourceId?: string;
  };
  const { isEmbed } = usePortalRoutes();
  const { redirectToCustomizedWebsiteOr } = useRedirectUrl();
  const { t } = useTranslation();
  const { api } = useGymflowModels();
  const { communicationProperties } = useClubSettings();
  const createLeadMutation = useMutationLeadCreateAsPublic({ api });

  const formik = useFormik<ContactUsType>({
    initialValues: {
      email: "",
      firstName: "",
      lastName: "",
      mobileNumber: "",
      // notes: "",
      leadSource: urlParams.leadSourceId
        ? { id: urlParams.leadSourceId }
        : (undefined as any),
      emailCommunication: communicationProperties.defaultEmailCommunication,
      smsCommunication: communicationProperties.defaultSmsCommunication,
    },
    validationSchema: toFormikValidationSchema<ContactUsType>(contactUsSchema),
    onSubmit: async () => {
      if (!formik.values.leadSource?.id) return;
      await createLeadMutation.mutateAsync({
        email: formik.values.email.trim().toLowerCase(),
        firstName: formik.values.firstName,
        lastName: formik.values.lastName,
        mobileNumber: formik.values.mobileNumber,
        sourceId: formik.values.leadSource?.id,
        emailCommunication: formik.values.emailCommunication,
        smsCommunication: formik.values.smsCommunication,
      });
      setTimeout(() => {
        redirectToCustomizedWebsiteOr();
      }, 1000);
    },
  });
  const [parent] = useAutoAnimate();
  return (
    <div
      className={cn(
        "track-height flex w-full flex-col overflow-y-auto items-center",
        {
          "h-fit": isEmbed,
          "h-full": !isEmbed,
        },
      )}
    >
      <div
        ref={parent}
        className="flex w-[calc(min(28rem,100vw-2rem))] flex-col gap-4 p-4"
      >
        {createLeadMutation.isSuccess && (
          <div className="flex flex-col items-center justify-center gap-1 py-6 pt-24">
            <LayeredCircles intent="success">
              <CheckmarkIcon pathClassName="stroke-success-600" />
            </LayeredCircles>
            <div className="dark:text-gray-0 text-center text-xl font-bold text-gray-950">
              {t("pages.hostedPagesLeadCapture.successTitle")}
            </div>
            <div className="text-md text-center text-gray-500">
              {t("pages.hostedPagesLeadCapture.successDescription")}
            </div>
          </div>
        )}
        {!createLeadMutation.isSuccess && (
          <>
            {!isEmbed && (
              <div className="flex flex-col items-center justify-center gap-1">
                <div className="dark:text-gray-0 text-center text-xl font-bold text-gray-950">
                  {t("pages.hostedPagesLeadCapture.title")}
                </div>
                <div className="text-md text-center text-gray-500">
                  {t("pages.hostedPagesLeadCapture.description")}
                </div>
              </div>
            )}
            <FormikProvider value={formik}>
              <FormInput<ContactUsType>
                name="email"
                label={t("portal.common.userMemberForm.email")}
                placeholder={t("portal.common.userMemberForm.email")}
                isRequired
              />
              <div className="flex flex-row gap-2 ">
                <FormInput<ContactUsType>
                  name="firstName"
                  label={t("portal.common.userMemberForm.firstName")}
                  placeholder={t("portal.common.userMemberForm.firstName")}
                  isRequired
                />
                <FormInput<ContactUsType>
                  name="lastName"
                  label={t("portal.common.userMemberForm.lastName")}
                  placeholder={t("portal.common.userMemberForm.lastName")}
                  isRequired
                />
              </div>
              <FormPhoneNumberInput<ContactUsType>
                name="mobileNumber"
                placeholder={t("portal.common.userMemberForm.mobileNumber")}
                label={t("portal.common.userMemberForm.mobileNumber")}
                isRequired
              />
              {/* <FormTextarea<ContactUsType>
                      name="notes"
                      label={t("portal.common.userMemberForm.notes")}
                      rows={5}
                      isRequired
                    /> */}
              {!urlParams.leadSourceId && (
                <FormSelectInput<ContactUsType, LeadSourceDTO>
                  name="leadSource"
                  label={t("portal.common.userMemberForm.leadSource")}
                  placeholder={t("portal.common.userMemberForm.leadSource")}
                  dataFetchingQuery={() =>
                    infiniteQueryOptions({
                      ...leadSourceListAsPublicQueryOptions(api),
                      select: (data) => {
                        const { pageParams, pages } = data as InfiniteData<
                          LeadSourceDTO[]
                        >;

                        const transformedPages = pages.map((page) =>
                          page.map((leadSource) => ({
                            id: leadSource.id,
                            label: leadSource.name,
                            value: leadSource,
                          })),
                        );
                        return {
                          pageParams,
                          pages: transformedPages,
                        };
                      },

                      getNextPageParam: () => undefined,
                      initialPageParam: undefined,
                    })
                  }
                  isRequired
                />
              )}
              <FormNotificationPreferences
                emailCommunicationFieldName={"emailCommunication"}
                smsCommunicationFieldName={"smsCommunication"}
              />
              <Button
                intent="secondary"
                className={cn({
                  "w-full": !isEmbed,
                  "w-fit self-center px-16": isEmbed,
                })}
                onClick={async () => await formik.submitForm()}
              >
                {t("pages.hostedPagesLeadCapture.submit")}
              </Button>
              {isEmbed && (
                <div
                  className={cn(
                    "w-full text-gray-500 flex items-center justify-center px-4 text-sm",
                  )}
                >
                  Powered by&nbsp;
                  <a
                    className="text-primary-600 hover:!text-primary-600 underline"
                    href="https://gymflow.io"
                    target="_blank"
                    rel="noreferrer"
                  >
                    Gymflow.io
                  </a>
                </div>
              )}
            </FormikProvider>
          </>
        )}
      </div>
    </div>
  );
};

export default HostedPagesLeadCapture;
